import {
    Button,
    Chip,
    Table,
    TableBody,
    TableContainer,
    TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Dispatch, SetStateAction } from "react";
import {
    dateTimeConvertor,
    isMoreThanOneHourInFuture,
} from "src/utils/helperFunctions";
import EmptyTable from "../table/EmptyTable";
import AllBidsHeadRow from "./AllBidsHeadRow";
import {
    BackorderData,
    HeadingType,
    OrderPropsType,
    SelectedBid,
} from "src/types/allBidsPageTypes";
import ProgressStatusChip from "../common/ProgressStatusChip";
import {
    ProgressStatusEnum,
    UserStatusEnum,
} from "../../enums/allBidsPageEnums";
import LoadingTable from "../table/LoadingTable";
import Big from "big.js";
import { AuctionStatusEnum } from "../../types/postAuctionTypes";
import { BsPencilSquare } from "react-icons/bs";
import Grid from "@mui/material/Grid";
import { StyledTableCell } from "../SharedStyles";
import { BackordersActionMenu } from "./ActionMenu";
import {
    bidAmountEditButtonSX,
    bidAmountEditIconSX,
    preBackorderChipSX,
    preBidsTableContainerSX,
    preBidsTableSX,
} from "./styles";

const amountUpdateAllowedBidStatus = [
    ProgressStatusEnum.INPROGRESS,
    ProgressStatusEnum.WAITING,
];

const headRow: HeadingType[] = [
    {
        id: "domainName",
        label: "Domain",
        align: "left",
        sorting: true,
    },
    {
        id: "priority",
        label: "Priority",
        align: "center",
        sorting: true,
        info: "You can select the priority for the bid that you are adding. If your priority is the highest, the domain is yours. If there are multiple bidders with the same highest priority, the system will create an auction.",
    },
    {
        id: "amount",
        label: "My Bid ($)",
        align: "right",
        sorting: true,
    },
    {
        id: "status",
        label: "Progress Status",
        align: "center",
    },
    {
        id: "dropTime",
        label: "Drop Date",
        align: "center",
        sorting: true,
    },
    {
        id: "bidders",
        label: "Bidders",
        align: "center",
        sorting: true,
    },
    {
        id: "action",
        label: "Actions",
        align: "center",
    },
];

const StyledTableRow = styled(TableRow)(() => ({
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

type BackorderTable = {
    backorderData: BackorderData[];
    orderProps: OrderPropsType;
    setOrderProps: Dispatch<SetStateAction<OrderPropsType>>;
    updateBidAmount: (selectedBid: SelectedBid) => void;
    deleteBid: (selectedBid: SelectedBid) => void;
    loading?: boolean;
};

const BackorderTable = (props: BackorderTable) => {
    const {
        backorderData,
        orderProps,
        setOrderProps,
        updateBidAmount,
        deleteBid,
        loading,
    } = props;

    const convertStatus = (
        status: ProgressStatusEnum,
        auctionStatus: AuctionStatusEnum,
    ) => {
        if (
            status === ProgressStatusEnum.SUCCESS &&
            auctionStatus == AuctionStatusEnum.PAYMENT_PENDING
        ) {
            return UserStatusEnum.PAYMENT;
        }

        if (
            status === ProgressStatusEnum.WAITING ||
            status === ProgressStatusEnum.INPROGRESS
        ) {
            return UserStatusEnum.INPROGRESS;
        }

        if (status === ProgressStatusEnum.PENDING) {
            return UserStatusEnum.SUBMISSIONPEND;
        }

        if (status === ProgressStatusEnum.RESULT_PENDING) {
            return UserStatusEnum.RESULT_PENDING;
        }

        return UserStatusEnum.PENDING;
    };

    const getDate = (date: number) => {
        return date ? dateTimeConvertor(+date).date : "";
    };

    const isAmountUpdateAllowed = (
        status: ProgressStatusEnum,
        dropTimeSeconds: number,
    ) => {
        const isAllowed =
            amountUpdateAllowedBidStatus.includes(status) &&
            isMoreThanOneHourInFuture(dropTimeSeconds);

        return isAllowed;
    };

    return (
        <TableContainer sx={preBidsTableContainerSX}>
            <Table sx={preBidsTableSX}>
                <AllBidsHeadRow
                    order={orderProps.order}
                    orderBy={orderProps.orderBy}
                    headRow={headRow}
                    setOrderProps={setOrderProps}
                />
                <TableBody>
                    {loading ? (
                        <LoadingTable colspan={headRow.length} />
                    ) : backorderData.length > 0 ? (
                        backorderData.map((row) => {
                            const status = convertStatus(
                                row.status,
                                row.auctionStatus,
                            );
                            return (
                                <StyledTableRow key={row.domainName}>
                                    <StyledTableCell
                                        align="left"
                                        component="th"
                                        scope="row"
                                    >
                                        <Grid
                                            container
                                            display="flex"
                                            justifyContent={"flex-start"}
                                            alignItems={"center"}
                                            columnSpacing={1}
                                        >
                                            <Grid item>{row.domainName}</Grid>
                                            {row.isPreBackorder && (
                                                <Grid
                                                    item
                                                    display={"flex"}
                                                    alignItems={"center"}
                                                >
                                                    <Chip
                                                        label="PRE"
                                                        color="primary"
                                                        variant="outlined"
                                                        size="small"
                                                        sx={preBackorderChipSX}
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="center"
                                        component="th"
                                        scope="row"
                                    >
                                        {row.priority}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="right"
                                        component="th"
                                        scope="row"
                                    >
                                        <Grid
                                            container
                                            display="flex"
                                            justifyContent={"flex-end"}
                                            columnGap={1}
                                        >
                                            <Grid item>
                                                {row.amount
                                                    ? Big(row.amount).toFixed(2)
                                                    : null}
                                            </Grid>
                                            <Grid item>
                                                {isAmountUpdateAllowed(
                                                    row.status,
                                                    row.dropTime,
                                                ) && (
                                                    <Button
                                                        onClick={() => {
                                                            updateBidAmount({
                                                                bidId: row.id,
                                                                domainName:
                                                                    row.domainName,
                                                                priority:
                                                                    row.priority,
                                                                bidAmount:
                                                                    Number(
                                                                        row.amount,
                                                                    ),
                                                                isPreBackorder:
                                                                    row.isPreBackorder,
                                                            });
                                                        }}
                                                        sx={
                                                            bidAmountEditButtonSX
                                                        }
                                                    >
                                                        <BsPencilSquare
                                                            style={
                                                                bidAmountEditIconSX
                                                            }
                                                        />
                                                    </Button>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </StyledTableCell>

                                    <StyledTableCell align="center">
                                        <ProgressStatusChip chipType={status} />
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="center"
                                        component="th"
                                        scope="row"
                                    >
                                        {row.status !=
                                        ProgressStatusEnum.PENDING
                                            ? getDate(row.dropTime).toString()
                                            : null}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="center"
                                        component="th"
                                        scope="row"
                                    >
                                        {row.bidders}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="center"
                                        component="th"
                                        scope="row"
                                    >
                                        <BackordersActionMenu
                                            data={row}
                                            deleteBid={deleteBid}
                                        />
                                    </StyledTableCell>
                                </StyledTableRow>
                            );
                        })
                    ) : (
                        <EmptyTable msg="No bids" colSpan={headRow.length} />
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default BackorderTable;
